import { useEffect, useState } from "react";
import { useBulkCampaigns } from "./useBulkCampaigns";

import CampaignsLoading from "V2.0/HomePages/HomeCampaign/common/CampaignLoading";
import { Heading } from "V2.0/common/heading";
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownLabel,
  DropdownSection,
} from "V2.0/common/dropdown";
import { PlusIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { CAMPAIGN_TYPE } from "V2.0/HomePages/HomeCampaign/SingleCampaignAdd";
import CampaignsEmpty from "V2.0/HomePages/HomeCampaign/common/CampaignEmpty";
import { BulkCampaignCreate } from "./BulkCampaignCreate";
import BulkCampaignsTable from "./BulkCampaignTable";
// BulkCampaignTabs/BulkCampaignTableHeader (Task 4) — the campaign-type tab bar and the sticky,
// column-aligned table header that replace the old flat list. (bulk-campaign-report-ui-port, Task 8)
import BulkCampaignTabs from "./BulkCampaignTabs";
import BulkCampaignTableHeader, {
  BULK_CAMPAIGN_TABLE_CONFIG,
} from "./BulkCampaignTableHeader";
import BulkCampaignPage from "./BulkCampaignPage";
import BulkCampaignDelete from "./BulkCampaignDelete";
import { CampaignTypeIcons } from "../common/CampaignTypeIcons";
import BulkCampaignResume from "./BulkCampaignResume";
import BulkCampaignPause from "./BulkCampaignPause";
import BulkCampaignCancel from "./BulkCampaignCancel";
import BulkCampaignReportModal from "./BulkCampaign/Components/BulkCampaignReportModal";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";

// The report modal's Export button reuses the CSV download the table row already offers, so
// it needs the same lifecycle callbacks. Kept separate from the table's copy because this one
// always has a campaign name to show.
const bulkCampaignReportDownloadLifecycleCbs = {
  init: (name) => {
    message.loading(name ? `Downloading ${name}` : "Downloading");
  },
  success: (filename, campaignName) => {
    message.destroy();
    message.success(`Downloaded ${campaignName} to ${filename}.csv`);
  },
  error: (e) => {
    message.destroy();
    showErrorToast(e, "Failed to download");
  },
};

export default function BulkCampaigns(props) {
  const { workspaceId, assistantsRecord, isViewOnly } = props;

  const {
    bulkCampaignsData,
    isBulkCampaignsLoading,
    fetchBulkCampaigns,
    createBulkCampaign,
    setBulkCampaigns,
    removeBulkCampaign,
    downloadBulkCampaignData,
    resumeBulkCampaign,
    pauseBulkCampaign,
    cancelBulkCampaign,
  } = useBulkCampaigns(workspaceId);

  const [createCampaign, setCreateCampaign] = useState({
    isOpen: false,
    campaignType: undefined,
  });
  const [activeBulkCampaign, setActiveBulkCampaign] = useState({
    bulkCampaign: null,
    isActive: false,
  });
  const [deleteCampaign, setDeleteCampaign] = useState({
    isOpen: false,
    bulkCampaign: undefined,
  });
  const [resumeCampaign, setResumeCampaign] = useState(null);
  const [pauseCampaign, setPauseCampaign] = useState(null);
  const [cancelCampaign, setCancelCampaign] = useState(null);
  // activeTab (Task 8): drives both BulkCampaignTabs and which campaigns are shown, defaulting to
  // CALL per the plan brief.
  const [activeTab, setActiveTab] = useState(CAMPAIGN_TYPE.CALL);
  const [bulkCampaignReport, setBulkCampaignReport] = useState({
    isOpen: false,
    bulkCampaign: undefined
  })

  useEffect(() => {
    if (workspaceId) {
      fetchBulkCampaigns();
    }
  }, [workspaceId]);

  // Old: table showed every campaign regardless of type. Replaced — the new tab bar (Task 4) filters
  // the list down to whichever channel is active. (bulk-campaign-report-ui-port, Task 8)
  // const isCampaignsEmpty =
  //   !isBulkCampaignsLoading && !Boolean(bulkCampaignsData.length);
  const filteredBulkCampaigns = bulkCampaignsData.filter(
    (c: any) => c.campaignType === activeTab,
  );

  const isCampaignsEmpty =
    !isBulkCampaignsLoading && !Boolean(filteredBulkCampaigns.length);

  return (
    <>
      <div className="flex flex-col gap-y-4 p-4 px-8 h-full w-full min-w-0 overflow-scroll">
        {/* Header */}
        {!activeBulkCampaign.isActive && (
          <div className="flex w-full flex-wrap items-end justify-between gap-4 mb-8 dark:border-white/10">
            <Heading>Bulk Campaigns</Heading>

            <div className="flex gap-4">
              {!isViewOnly && (
                <div className="flex gap-x-2">
                  <Dropdown>
                    <DropdownButton>
                      <PlusIcon /> Start Campaign
                      <ChevronDownIcon />
                    </DropdownButton>
                    <DropdownMenu className="min-w-48">
                      <DropdownSection>
                        <DropdownItem
                          onClick={() =>
                            setCreateCampaign({
                              isOpen: true,
                              campaignType: CAMPAIGN_TYPE.SMS,
                            })
                          }
                        >
                          <div className="flex items-center gap-x-2">
                            <div>{CampaignTypeIcons[CAMPAIGN_TYPE.SMS]}</div>
                            <p className="text-sm">Sms</p>
                          </div>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            setCreateCampaign({
                              isOpen: true,
                              campaignType: CAMPAIGN_TYPE.CALL,
                            })
                          }
                        >
                          <div className="flex items-center gap-x-2">
                            <div>{CampaignTypeIcons[CAMPAIGN_TYPE.CALL]}</div>
                            <p className="text-sm">Call</p>
                          </div>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            setCreateCampaign({
                              isOpen: true,
                              campaignType: CAMPAIGN_TYPE.WHATSAPP,
                            })
                          }
                        >
                          <div className="flex items-center gap-x-2">
                            <div>
                              {CampaignTypeIcons[CAMPAIGN_TYPE.WHATSAPP]}
                            </div>
                            <p className="text-sm">Whatsapp</p>
                          </div>
                        </DropdownItem>
                        {/* <DropdownItem
                        onClick={() =>
                          setCreateCampaign({
                            isOpen: true,
                            campaignType: CAMPAIGN_TYPE.EMAIL,
                          })
                        }
                      >
                        <div className="flex items-center gap-x-2">
                          <div>{CampaignTypeIcons[CAMPAIGN_TYPE.EMAIL]}</div>
                          <p className="text-sm">Email</p>
                        </div>
                      </DropdownItem> */}
                      </DropdownSection>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        {/* Old body: no tabs, empty state at page level, no loading state in body, plain flat table.
            Replaced with the tabbed layout below — BulkCampaignTabs (Task 4) filters by channel, the
            sticky BulkCampaignTableHeader (Task 4) keeps column labels aligned with each row's stat
            columns (Task 3/5), and loading/empty now render inside the bordered table container so
            they appear under the tab bar instead of displacing it. (bulk-campaign-report-ui-port,
            Task 8 Step 2 — adopted Frontend's loading/empty placement since the tabs now always need
            to stay visible, including while loading/empty, same as Frontend does.) */}
        {/* {isCampaignsEmpty && <CampaignsEmpty />}

        {!activeBulkCampaign.isActive && !isCampaignsEmpty && (
          <BulkCampaignsTable
            bulkCampaigns={bulkCampaignsData}
            isLoading={isBulkCampaignsLoading}
            onBulkCampaignClick={(bulkCampaign) => {
              setActiveBulkCampaign({
                isActive: true,
                bulkCampaign,
              });
            }}
            onBulkCampaignDelete={(bulkCampaign) => {
              setDeleteCampaign({
                isOpen: true,
                bulkCampaign,
              });
            }}
            onBulkCampaignReport={(bulkCampaign) => {
              setBulkCampaignReport({
                isOpen: true,
                bulkCampaign
              })
            }}
            onBulkCampaignDownload={(bulkCampaign, lifecycleCbs) => {
              downloadBulkCampaignData(bulkCampaign, lifecycleCbs);
            }}
            onBulkCampaignResume={(bulkCampaign: any) =>
              setResumeCampaign(bulkCampaign)
            }
            onBulkCampaignPause={(bulkCampaign: any) =>
              setPauseCampaign(bulkCampaign)
            }
            onBulkCampaignCancel={(bulkCampaign: any) =>
              setCancelCampaign(bulkCampaign)
            }
            isViewOnly={isViewOnly}
          />
        )} */}

        {!activeBulkCampaign.isActive && (
          <div className="flex-1 min-h-0 flex flex-col">
            <BulkCampaignTabs activeTab={activeTab} onTabChange={setActiveTab} />
            <div className="border border-zinc-200 rounded-lg rounded-tl-none bg-white flex-1 min-h-0 flex flex-col overflow-auto">
              {isBulkCampaignsLoading && <CampaignsLoading />}
              {isCampaignsEmpty && !isBulkCampaignsLoading && <CampaignsEmpty />}
              {!isCampaignsEmpty && !isBulkCampaignsLoading && (
                <div
                  style={{
                    minWidth: BULK_CAMPAIGN_TABLE_CONFIG[activeTab]?.minWidth,
                  }}
                >
                  <BulkCampaignTableHeader campaignType={activeTab} />
                  <BulkCampaignsTable
                    bulkCampaigns={filteredBulkCampaigns}
                    isLoading={isBulkCampaignsLoading}
                    onBulkCampaignClick={(bulkCampaign) => {
                      setActiveBulkCampaign({
                        isActive: true,
                        bulkCampaign,
                      });
                    }}
                    onBulkCampaignDelete={(bulkCampaign) => {
                      setDeleteCampaign({
                        isOpen: true,
                        bulkCampaign,
                      });
                    }}
                    onBulkCampaignReport={(bulkCampaign: any) => {
                      setBulkCampaignReport({
                        isOpen: true,
                        bulkCampaign,
                      });
                    }}
                    onBulkCampaignDownload={(bulkCampaign, lifecycleCbs) => {
                      downloadBulkCampaignData(bulkCampaign, lifecycleCbs);
                    }}
                    onBulkCampaignResume={(bulkCampaign: any) =>
                      setResumeCampaign(bulkCampaign)
                    }
                    onBulkCampaignPause={(bulkCampaign: any) =>
                      setPauseCampaign(bulkCampaign)
                    }
                    onBulkCampaignCancel={(bulkCampaign: any) =>
                      setCancelCampaign(bulkCampaign)
                    }
                    isViewOnly={isViewOnly}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeBulkCampaign.isActive && (
          <BulkCampaignPage
            workspaceId={workspaceId}
            bulkCampaign={activeBulkCampaign.bulkCampaign}
            assistantsRecord={assistantsRecord}
            onBack={() =>
              setActiveBulkCampaign({
                isActive: false,
                bulkCampaign: null,
              })
            }
            isViewOnly={isViewOnly}
          />
        )}
      </div>

      {/* Create Campaign form */}
      {createCampaign.isOpen && (
        <BulkCampaignCreate
          dialogState={createCampaign}
          campaignType={createCampaign.campaignType}
          workspaceId={workspaceId}
          createBulkCampaign={createBulkCampaign}
          onClose={() =>
            setCreateCampaign({
              isOpen: false,
              campaignType: undefined,
            })
          }
        />
      )}

      {/* Delete Campaign */}
      {deleteCampaign.isOpen && (
        <BulkCampaignDelete
          bulkCampaign={deleteCampaign.bulkCampaign}
          onClose={() =>
            setDeleteCampaign({
              isOpen: false,
              bulkCampaign: undefined,
            })
          }
          onBulkCampaignDelete={(bulkCampaignId, lifecycleCbs) =>
            removeBulkCampaign(bulkCampaignId, lifecycleCbs)
          }
        />
      )}

      {resumeCampaign && (
        <BulkCampaignResume
          bulkCampaign={resumeCampaign}
          onBulkCampaignResume={resumeBulkCampaign}
          onClose={() => setResumeCampaign(null)}
        />
      )}

      {pauseCampaign && (
        <BulkCampaignPause
          bulkCampaign={pauseCampaign}
          onBulkCampaignPause={pauseBulkCampaign}
          onClose={() => setPauseCampaign(null)}
        />
      )}

      {cancelCampaign && (
        <BulkCampaignCancel
          bulkCampaign={cancelCampaign}
          onBulkCampaignCancel={cancelBulkCampaign}
          onClose={() => setCancelCampaign(null)}
        />
      )}

      {bulkCampaignReport.isOpen && (
        <BulkCampaignReportModal
          isOpen={bulkCampaignReport.isOpen}
          onClose={() => setBulkCampaignReport({
            isOpen: false,
            bulkCampaign: undefined
          })}
          workspaceId={workspaceId}
          bulkCampaign={bulkCampaignReport.bulkCampaign}
          onExport={(bulkCampaign) =>
            downloadBulkCampaignData(bulkCampaign, bulkCampaignReportDownloadLifecycleCbs)
          }
        />
      )}

    </>
  );
}
